/**
 * A form that takes over the whole view, the way Manage plans hands the Credit
 * Cards view to PayoffPlanBuilder. Nothing else is on screen to confuse the
 * form with, so it needs no accent, no lift and no dimming — the separation is
 * structural.
 */

import React, { useLayoutEffect, useRef } from 'react';
import { PageHeader, FormSection, StickyActionBar, Button, Tag } from './primitives';

interface FormScreenProps {
  /** `‹ Back to expenses` */
  backLabel: string;
  heading: string;
  /** The stored name of the thing being edited, shown as a Tag beside the
   *  heading. Pass the SAVED value, not the draft — otherwise it changes under
   *  the reader as they retype the name field. */
  subject?: string;
  sectionTitle: string;
  hint?: React.ReactNode;
  submitLabel: string;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  children: React.ReactNode;
}

export const FormScreen: React.FC<FormScreenProps> = ({
  backLabel,
  heading,
  subject,
  sectionTitle,
  hint,
  submitLabel,
  onSubmit,
  onCancel,
  children,
}) => (
  // Fill what is left of the viewport. Without this a form shorter than the
  // screen leaves `position: sticky` no travel, and the action bar just sits
  // wherever the content ends — looking broken rather than pinned.
  //
  // --app-header-h is measured from the real fixed header in App.tsx, so this
  // stays correct as the nav wraps. The 90px cancels Page's pb-[90px], putting
  // the bar exactly on the viewport bottom with no leftover scroll.
  <form
    onSubmit={onSubmit}
    className="flex flex-col min-h-[calc(100vh-var(--app-header-h))] -mb-[90px]"
  >
    <PageHeader
      backLabel={backLabel}
      onBack={onCancel}
      heading={heading}
      adornment={subject ? <Tag weight="bone">{subject}</Tag> : undefined}
    />

    <FormSection className="mt-8" title={sectionTitle} index="01" hint={hint}>
      {children}
    </FormSection>

    {/* The form keeps its min-height so the sticky bar has travel, but the
        card must not absorb it — a flex-1 card stretches to the viewport and
        ends in a tall empty box. The slack lives here instead. */}
    <div className="flex-1" aria-hidden="true" />

    <StickyActionBar>
      <Button type="button" variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="submit" variant="primary">
        {submitLabel}
      </Button>
    </StickyActionBar>
  </form>
);

/**
 * Keeps the document in the right place across the swap between a list and a
 * full-screen form. Without it, opening a form from halfway down a list leaves
 * you looking at the middle of the form.
 *
 * Returns a function to call from the handler that opens the form, so the
 * list's position is captured before the view changes.
 */
export function useFormScreenScroll(isFormOpen: boolean): () => void {
  const listScroll = useRef(0);

  useLayoutEffect(() => {
    window.scrollTo(0, isFormOpen ? 0 : listScroll.current);
  }, [isFormOpen]);

  return () => {
    listScroll.current = window.scrollY;
  };
}
